// Pagina dettaglio materiale: info, stock per magazzino, seriali, modifica
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, Pencil, Loader2 } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Input } from '../../components/ui/Input'
import api from '../../lib/api'

// --- Types ---

interface WarehouseStock {
  warehouseId: string
  warehouseName: string
  quantity: number
  minQuantity: number
}

interface MaterialSerial {
  id: string
  serialNumber: string
  partNumber?: string
  location?: string
  notes?: string
  status: 'available' | 'in_use' | 'transferred' | 'damaged'
}

interface MaterialDetail {
  id: string
  code: string
  name: string
  description?: string | null
  unit?: string | null
  unitCost?: number | null
  category?: string | null
  barcode?: string | null
  supplier?: string | null
  minStock?: number | null
  location?: string | null
  isActive: boolean
  warehouseStocks: WarehouseStock[]
  serials: MaterialSerial[]
  totalQuantity: number
}

// --- Edit form schema ---

const editSchema = z.object({
  name: z.string().min(1, 'Nome obbligatorio'),
  description: z.string().optional(),
  category: z.string().optional(),
  unit: z.string().optional(),
  unitCost: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().positive().optional(),
  ),
  supplier: z.string().optional(),
  barcode: z.string().optional(),
  minStock: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().int().min(0).optional(),
  ),
  location: z.string().optional(),
})

type EditFormData = z.infer<typeof editSchema>

// --- Status badge helper ---

const statusConfig: Record<MaterialSerial['status'], { label: string; className: string }> = {
  available: { label: 'Disponibile', className: 'bg-green-100 text-green-700' },
  in_use: { label: 'In uso', className: 'bg-yellow-100 text-yellow-700' },
  transferred: { label: 'Trasferito', className: 'bg-blue-100 text-blue-700' },
  damaged: { label: 'Danneggiato', className: 'bg-red-100 text-red-700' },
}

// --- Component ---

export function MaterialDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)

  const { data: material, isLoading, isError } = useQuery<MaterialDetail>({
    queryKey: ['material-detail', id],
    queryFn: () => api.get<MaterialDetail>(`/warehouse/materials/${id}`).then((r) => r.data),
    enabled: !!id,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
  })

  const editMutation = useMutation({
    mutationFn: (data: EditFormData) => api.patch(`/warehouse/materials/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['material-detail', id] })
      void queryClient.invalidateQueries({ queryKey: ['materials'] })
      setEditOpen(false)
    },
  })

  const openEdit = () => {
    if (!material) return
    reset({
      name: material.name ?? '',
      description: material.description ?? '',
      category: material.category ?? '',
      unit: material.unit ?? '',
      unitCost: material.unitCost ?? undefined,
      supplier: material.supplier ?? '',
      barcode: material.barcode ?? '',
      minStock: material.minStock ?? 0,
      location: material.location ?? '',
    })
    setEditOpen(true)
  }

  const textareaClass =
    'w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--fc-primary)]" />
      </div>
    )
  }

  if (isError || !material) {
    return (
      <div className="space-y-4">
        <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/warehouse/materials')}>
          Torna ai materiali
        </Button>
        <p className="text-sm text-[var(--fc-danger)]">Materiale non trovato o errore nel caricamento.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={material.name}
        breadcrumb={[
          { label: 'Magazzino', href: '/warehouse' },
          { label: 'Materiali', href: '/warehouse/materials' },
          { label: material.name },
        ]}
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/warehouse/materials')}
            >
              Indietro
            </Button>
            <Button leftIcon={<Pencil className="h-4 w-4" />} onClick={openEdit}>
              Modifica
            </Button>
          </div>
        }
      />

      {/* Informazioni generali */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[var(--fc-foreground)]">Informazioni</h2>
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          <InfoRow label="Codice" value={<span className="font-mono text-sm">{material.code}</span>} />
          <InfoRow label="Nome" value={material.name} />
          <InfoRow label="Categoria" value={material.category ?? '—'} />
          <InfoRow label="Unità di misura" value={material.unit ?? '—'} />
          <InfoRow label="Fornitore" value={material.supplier ?? '—'} />
          <InfoRow label="Barcode" value={material.barcode ? <span className="font-mono text-sm">{material.barcode}</span> : '—'} />
          {material.description && (
            <div className="sm:col-span-2 lg:col-span-3">
              <InfoRow label="Descrizione" value={material.description} />
            </div>
          )}
        </dl>
      </Card>

      {/* Stock per magazzino */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[var(--fc-foreground)]">Stock per magazzino</h2>
          <span className="text-sm text-[var(--fc-foreground-muted)]">
            Totale: <strong className="text-[var(--fc-foreground)]">{material.totalQuantity}</strong> {material.unit ?? 'pz'}
          </span>
        </div>
        {material.warehouseStocks.length === 0 ? (
          <p className="py-4 text-center text-sm text-[var(--fc-foreground-muted)]">Nessun stock registrato.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-[var(--fc-border)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--fc-surface-overlay)]">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Magazzino</th>
                  <th className="px-4 py-2 text-right font-medium text-[var(--fc-foreground-muted)]">Pezzi</th>
                  <th className="px-4 py-2 text-right font-medium text-[var(--fc-foreground-muted)]">Scorta min</th>
                </tr>
              </thead>
              <tbody>
                {material.warehouseStocks.map((s) => (
                  <tr key={s.warehouseId} className="border-t border-[var(--fc-border)]">
                    <td className="px-4 py-2 text-[var(--fc-foreground)]">{s.warehouseName}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${s.quantity <= s.minQuantity && s.minQuantity > 0 ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-foreground)]'}`}>
                      {s.quantity}
                    </td>
                    <td className="px-4 py-2 text-right text-[var(--fc-foreground-muted)]">{s.minQuantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Numeri seriali */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[var(--fc-foreground)]">Numeri seriali</h2>
          <span className="text-sm text-[var(--fc-foreground-muted)]">{material.serials.length} seriali</span>
        </div>
        {material.serials.length === 0 ? (
          <p className="py-4 text-center text-sm text-[var(--fc-foreground-muted)]">Nessun numero seriale registrato.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto rounded border border-[var(--fc-border)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[var(--fc-surface-overlay)]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">S/N</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">P/N</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Ubicazione</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Stato</th>
                  <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Note</th>
                </tr>
              </thead>
              <tbody>
                {material.serials.map((s) => {
                  const cfg = statusConfig[s.status] ?? { label: s.status, className: '' }
                  return (
                    <tr key={s.id} className="border-t border-[var(--fc-border)]">
                      <td className="px-3 py-2 font-mono text-xs text-[var(--fc-foreground)]">{s.serialNumber}</td>
                      <td className="px-3 py-2 font-mono text-xs text-[var(--fc-foreground-muted)]">{s.partNumber ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-[var(--fc-foreground-muted)]">{s.location ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${cfg.className}`}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-[var(--fc-foreground-muted)]">{s.notes ?? '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal modifica */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Modifica: ${material.name}`}
        size="lg"
      >
        <form onSubmit={handleSubmit((data) => editMutation.mutate(data))} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Nome *"
              {...register('name')}
              error={errors.name?.message}
              placeholder="Nome materiale"
            />
            <Input
              label="Categoria"
              {...register('category')}
              placeholder="Filtri, Ricambi..."
            />
            <Input
              label="Unità di misura"
              {...register('unit')}
              placeholder="pz, kg, m..."
            />
            <Input
              label="Costo unitario (€)"
              type="number"
              step="0.01"
              {...register('unitCost')}
              error={errors.unitCost?.message}
              placeholder="12.50"
            />
            <Input
              label="Fornitore"
              {...register('supplier')}
              placeholder="Nome fornitore"
            />
            <Input
              label="Barcode"
              {...register('barcode')}
              placeholder="EAN13, QR..."
            />
            <Input
              label="Scorta minima"
              type="number"
              {...register('minStock')}
              error={errors.minStock?.message}
              placeholder="5"
            />
            <Input
              label="Ubicazione"
              {...register('location')}
              placeholder="Scaffale A3..."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Descrizione</label>
            <textarea
              {...register('description')}
              rows={2}
              className={textareaClass}
              placeholder="Descrizione del materiale..."
            />
          </div>
          {editMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">Errore durante il salvataggio. Riprova.</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => setEditOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSubmitting || editMutation.isPending}>
              {editMutation.isPending ? 'Salvataggio...' : 'Salva modifiche'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

// --- Helper component ---

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-[var(--fc-foreground-muted)] uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-sm text-[var(--fc-foreground)]">{value}</dd>
    </div>
  )
}
